import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { jwtDecode } from 'jwt-decode';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const normalizarTurno = (turno) => ({
  ...turno,
  fecha_turno: turno?.fecha_turno
    ? (turno.fecha_turno instanceof Date
        ? turno.fecha_turno
        : new Date(`${turno.fecha_turno}T00:00:00`))
    : null,
  createdAt: turno?.createdAt ? new Date(turno.createdAt) : null,
  proveedor_bultos: Array.isArray(turno?.proveedor_bultos)
    ? turno.proveedor_bultos
    : typeof turno?.proveedor_bultos === 'string'
      ? (() => { try { return JSON.parse(turno.proveedor_bultos); } catch { return []; } })()
      : [],
  documentos: (Object.prototype.hasOwnProperty.call(turno || {}, 'documentos'))
    ? (Array.isArray(turno.documentos) ? turno.documentos : [])
    : undefined
});

const clip = (v, max) => String(v ?? '').slice(0, max);

const mapEmbalaje = (raw) => {
  const s = String(raw || '').trim().toUpperCase();
  if (s.startsWith('PA')) return { code: 'PA', text: 'Palets' };
  if (s.startsWith('UN')) return { code: 'UN', text: 'Unidades' };
  if (s.startsWith('CJ')) return { code: 'CJ', text: 'Cajas' };
  const code = clip(s.replace(/[^A-Z0-9]/g, ''), 2) || 'MI';
  return { code, text: clip(s || 'Mixto', 60) };
};

const embalajeDominanteProveedor = (prov) => {
  if (prov?.embalaje) return mapEmbalaje(prov.embalaje);
  const lista = Object.entries(prov?.bultos || {})
    .map(([tipo, q]) => [String(tipo), Number(q) || 0])
    .filter(([, q]) => q > 0)
    .sort((a, b) => b[1] - a[1]);
  if (lista.length) return mapEmbalaje(lista[0][0]);
  return { code: 'MI', text: 'Mixto' };
};

const getTipoSolicitante = (turno) => {
  const fromApi = (turno?.tipo_solicitante || '').toString().trim().toLowerCase();
  if (fromApi === 'proveedor') return { code: 'P', text: 'Proveedor' };
  if (fromApi === 'transportista') return { code: 'T', text: 'Transportista' };

  const isProveedor = Array.isArray(turno?.proveedor_bultos) && turno.proveedor_bultos.length > 0;
  return isProveedor ? { code: 'P', text: 'Proveedor' } : { code: 'T', text: 'Transportista' };
};

const getDocs = (t, { pinnedCacheRef, turnosRef }) => {
  if (Array.isArray(t?.documentos) && t.documentos.length > 0) return t.documentos;

  const cacheDocs = pinnedCacheRef.current?.[t?.id]?.documentos;
  if (Array.isArray(cacheDocs) && cacheDocs.length > 0) return cacheDocs;

  const prevDocs = (turnosRef.current || []).find(x => x.id === t?.id)?.documentos;
  if (Array.isArray(prevDocs) && prevDocs.length > 0) return prevDocs;

  return [];
};

const mergeCachePreservandoDocs = (prev = {}, incoming = {}) => ({
  ...prev,
  ...incoming,
  documentos:
    (Array.isArray(incoming.documentos) && incoming.documentos.length > 0)
      ? incoming.documentos
      : (Array.isArray(prev.documentos) ? prev.documentos : [])
});

const asegurarHoraEnLista = (lista, hora) =>
  hora && !lista.includes(hora) ? [...lista, hora].sort() : lista;

const AdminTurnos = () => {
  const navigate = useNavigate();
  const [turnos, setTurnos] = useState([]);
  const [error, setError] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [modalEdicionVisible, setModalEdicionVisible] = useState(false);
  const [fechasDisponibles, setFechasDisponibles] = useState([]);
  const [horasDisponibles, setHorasDisponibles] = useState([]);
  const [turnoSeleccionado, setTurnoSeleccionado] = useState(null);
  const [devolucion, setDevolucion] = useState('');
  const [expandedRows, setExpandedRows] = useState([]);
  const [modalRechazoVisible, setModalRechazoVisible] = useState(false);
  const [motivoRechazo, setMotivoRechazo] = useState('');
  const [turnoRechazo, setTurnoRechazo] = useState(null);
  const [motivoError, setMotivoError] = useState('');
  const [loadingRechazo, setLoadingRechazo] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshMs, setRefreshMs] = useState(60000);
  const [loadingLista, setLoadingLista] = useState(false);
  const lastRunRef = useRef(0);
  const intervalRef = useRef(null);

  const [pinnedIds, setPinnedIds] = useState(() => {
    try {
      const raw = localStorage.getItem('adminTurnos.pinnedIds');
      const arr = raw ? JSON.parse(raw) : [];
      return new Set(Array.isArray(arr) ? arr : []);
    } catch {
      return new Set();
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem('adminTurnos.pinnedIds', JSON.stringify([...pinnedIds]));
    } catch (e) {
      console.warn('No se pudo persistir pinnedIds:', e);
    }
  }, [pinnedIds]);

  const [pinnedCache, setPinnedCache] = useState(() => {
    try {
      const raw = localStorage.getItem('adminTurnos.pinnedCache');
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem('adminTurnos.pinnedCache', JSON.stringify(pinnedCache));
    } catch (e) {
      console.warn('No se pudo persistir pinnedCache:', e);
    }
  }, [pinnedCache]);

  const pinnedIdsRef = useRef(pinnedIds);
  useEffect(() => { pinnedIdsRef.current = pinnedIds; }, [pinnedIds]);

  const pinnedCacheRef = useRef(pinnedCache);
  useEffect(() => { pinnedCacheRef.current = pinnedCache; }, [pinnedCache]);

  const turnosRef = useRef([]);
  useEffect(() => { turnosRef.current = turnos; }, [turnos]);

  const verificarRol = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    try {
      const decoded = jwtDecode(token);
      if (decoded.rol !== 'admin') navigate('/');
    } catch (error) {
      console.error('❌ Error al decodificar el token:', error);
      navigate('/login');
    }
  }, [navigate]);

  const hidratarDesdeCache = useCallback((t) => {
    const cached = pinnedCacheRef.current?.[t.id];
    const prev = (turnosRef.current || []).find(x => x.id === t.id);
    const merged = { ...t };

    const tNom = t?.transportista?.nombre;
    if (!merged.transportista && (cached?.transportista || prev?.transportista)) {
      merged.transportista = cached?.transportista || prev?.transportista;
    } else if ((!tNom || tNom === '') && (cached?.transportista || prev?.transportista)) {
      merged.transportista = { ...(t.transportista || {}), ...(cached?.transportista || prev?.transportista || {}) };
    }

    const tieneProv = Array.isArray(merged.proveedor_bultos) && merged.proveedor_bultos.length > 0;
    const provCache = Array.isArray(cached?.proveedor_bultos) && cached.proveedor_bultos.length > 0;
    const provPrev  = Array.isArray(prev?.proveedor_bultos)   && prev.proveedor_bultos.length   > 0;
    if (!tieneProv && (provCache || provPrev)) {
      merged.proveedor_bultos = cached?.proveedor_bultos || prev?.proveedor_bultos || [];
    }

    const tieneDocs = Array.isArray(merged.documentos) && merged.documentos.length > 0;
    const docsCache = Array.isArray(cached?.documentos) && cached.documentos.length > 0;
    const docsPrev  = Array.isArray(prev?.documentos)   && prev.documentos.length   > 0;
    if (!tieneDocs && (docsCache || docsPrev)) {
      merged.documentos = cached?.documentos || prev?.documentos || [];
    }

    return merged;
  }, []);

  const fetchTurnosPendientes = useCallback(async () => {
    try {
      setLoadingLista(true);
      const token = localStorage.getItem('token');

      const response = await api.get('/turnos/pendientes', {
        headers: { Authorization: `Bearer ${token}` },
      });

      const nuevos = (response.data || [])
        .map(normalizarTurno)
        .map(hidratarDesdeCache);

      setPinnedCache(prev => {
        const next = { ...prev };
        for (const t of nuevos) {
          if (t.transportista) {
            next[t.id] = { ...(next[t.id] || {}), transportista: t.transportista };
          }
          if (Array.isArray(t.proveedor_bultos) && t.proveedor_bultos.length > 0) {
            next[t.id] = { ...(next[t.id] || {}), proveedor_bultos: t.proveedor_bultos };
          }
          if (Array.isArray(t.documentos) && t.documentos.length > 0) {
            next[t.id] = { ...(next[t.id] || {}), documentos: t.documentos };
          }
        }
        return next;
      });

      const nuevosIds = new Set(nuevos.map(t => t.id));
      const idsFijados = Array.from(pinnedIdsRef.current || []);
      const faltantes = idsFijados.filter(id => !nuevosIds.has(id));

      const fetchedFaltantes = await Promise.all(
        faltantes.map(async (id) => {
          try {
            const r = await api.get(`/turnos/${id}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            if (!r?.data) return null;
            const tNorm = normalizarTurno(r.data);

            const prev = pinnedCacheRef.current?.[id];
            const tWithDocs = mergeCachePreservandoDocs(prev, tNorm);

            setPinnedCache(prevCache => ({
              ...prevCache,
              [id]: mergeCachePreservandoDocs(prevCache?.[id], tNorm)
            }));

            return hidratarDesdeCache(tWithDocs);
          } catch (e) {
            console.warn(`No se pudo obtener el turno fijado ${id} por ID. Uso cache si existe.`, e);
            const fromCache = pinnedCacheRef.current?.[id];
            return fromCache ? normalizarTurno(fromCache) : null;
          }
        })
      );

      const extrasFijados = fetchedFaltantes.filter(Boolean);
      setTurnos([...nuevos, ...extrasFijados]);
      lastRunRef.current = Date.now();
    } catch (error) {
      console.error('❌ Error al obtener turnos pendientes:', error);
      setError('Error al obtener turnos pendientes.');
    } finally {
      setLoadingLista(false);
    }
  }, [hidratarDesdeCache]);

  const fetchFechasDisponibles = useCallback(async () => {
    try {
      const response = await api.get('/turnos/disponibles');
      setFechasDisponibles(response.data.fechas.map(fecha => new Date(fecha)));
    } catch (error) {
      setError('Error al obtener disponibilidad de fechas.');
    }
  }, []);

  useEffect(() => {
    const clear = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    if (!autoRefresh) { clear(); return; }

    const now = Date.now();
    if (now - lastRunRef.current > refreshMs) fetchTurnosPendientes();

    intervalRef.current = setInterval(() => {
      if (document.hidden) return;
      fetchTurnosPendientes();
    }, refreshMs);

    return clear;
  }, [autoRefresh, refreshMs, fetchTurnosPendientes]);

  useEffect(() => {
    const onVis = () => {
      if (!autoRefresh) return;
      if (!document.hidden) fetchTurnosPendientes();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [autoRefresh, fetchTurnosPendientes]);

/*   useEffect(() => {
    verificarRol();
    fetchTurnosPendientes();
    fetchFechasDisponibles();
  }, []); */
  useEffect(() => {
    verificarRol();
    fetchTurnosPendientes();
    fetchFechasDisponibles();
  }, [verificarRol, fetchTurnosPendientes, fetchFechasDisponibles]);  

  const toggleExpand = (turnoId) => {
    setExpandedRows((prev) =>
      prev.includes(turnoId)
        ? prev.filter(id => id !== turnoId)
        : [...prev, turnoId]
    );
  };

  const aprobarTurno = async (turnoId) => {
    try {
      const token = localStorage.getItem('token');
      const turno = turnos.find(t => t.id === turnoId);
      if (!turno) throw new Error('Turno no encontrado');

      await api.put(`/turnos/${turnoId}/estado`, { estado: 'aprobado' }, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const fechaLocal = new Date(turno.fecha_turno);
      if (isNaN(fechaLocal)) throw new Error('Fecha del turno inválida');

      const [hDesde, mDesde] = (turno.hora_desde || '').split(':');
      const [hHasta, mHasta] = (turno.hora_hasta || '').split(':');
      if (!hDesde || !mDesde) throw new Error('Hora desde inválida');
      if (!hHasta || !mHasta) throw new Error('Hora hasta inválida');

      const items = [];
      let totalDeclarado = 0;

      (turno.proveedor_bultos || []).forEach((prov) => {
        let facturas = [];
        if (Array.isArray(prov.facturas)) facturas = prov.facturas;
        else if (typeof prov.facturas === 'string') {
          facturas = prov.facturas.split(',').map(f => f.trim()).filter(Boolean);
        }
        if (facturas.length === 0) facturas = [''];

        const cantidadBase =
          Number(prov.cantidad) ||
          Object.values(prov.bultos || {}).reduce((acc, n) => acc + (Number(n) || 0), 0) ||
          0;

        const { code: embCode, text: embText } = embalajeDominanteProveedor(prov);

        facturas.forEach((fact) => {
          const cantidad = Number(cantidadBase);
          totalDeclarado += cantidad;

          items.push({
            Proveedor: String(prov.sap_id || prov.codigo || ''),
            ProveedorName: clip(String(prov.nombre || turno.transportista?.nombre || 'SIN NOMBRE'), 80),
            Factura: String(fact || ''),
            DocumentoCompra: String(prov.orden_compra || ''),
            Remito: String(prov.remito || ''),
            Embalaje: embCode,
            EmbalajeText: embText,
            CantidadDeclarada: cantidad,
            Observaciones: clip(String(turno.observaciones || ''), 80)
          });
        });
      });

      const primerProv = (turno.proveedor_bultos || [])[0] || {};
      const solicitanteRaw = String(
        primerProv.sap_id || primerProv.codigo || turno.transportista?.sap_id || '10002'
      ).replace(/\s+/g, '');

      const solicitante = /^\d+$/.test(solicitanteRaw)
        ? solicitanteRaw.padStart(5, '0')
        : solicitanteRaw;

      const tipoSol = getTipoSolicitante(turno);

      const y = fechaLocal.getFullYear();
      const m = fechaLocal.getMonth();
      const d = fechaLocal.getDate();
      const epochUTC = Date.UTC(y, m, d, 0, 0, 0, 0);
      const fechaOData = `/Date(${epochUTC})/`;

      const { code: embalajeCabeceraCode } = embalajeDominanteProveedor(primerProv);

      const obsCabecera = clip(
        turno.observaciones ||
        (primerProv.nombre ? `Turno de proveedor ${primerProv.nombre}` : '') ||
        'Turno aprobado desde Admin',
        60 
      );      

      const turnoSAP = {
        TipoSolicitante: tipoSol.text,
        Solicitante: solicitante,
        Fecha: fechaOData,
        HoraInicio: `PT${hDesde}H${mDesde}M00S`,
        HoraFin: `PT${hHasta}H${mHasta}M00S`,
        Embalaje: embalajeCabeceraCode,
        TotalDeclarado: Number(totalDeclarado),
        Anulado: false,
        Rechazado: false,
        Observaciones: obsCabecera,
        items
      };

      try {
        await api.post('/sap/crear-turno', turnoSAP, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
        });
        console.log('✅ Turno creado en SAP');
      } catch (sapError) {
        console.error('⚠️ Error al crear turno en SAP:', sapError);
        setError('Turno aprobado localmente, pero falló la sincronización con SAP.');
      }

      setPinnedIds(prev => {
        const next = new Set(prev);
        next.delete(turnoId);
        return next;
      });
      setPinnedCache(prev => {
        const next = { ...prev };
        delete next[turnoId];
        return next;
      });

      setMensaje('Turno aprobado correctamente.');
      await fetchTurnosPendientes();
    } catch (error) {
      console.error('❌ Error al aprobar turno:', error);
      setError(error.message || 'Error al aprobar el turno.');
    }
  };

  const confirmarRechazo = async () => {
    if (!turnoRechazo) return;

    const motivoTrim = (motivoRechazo || '').trim();
    if (motivoTrim.length < 5) {
      setMotivoError('El motivo es demasiado corto. Agrega un poco más de detalle (mín. 5 caracteres).');
      return;
    }
    if (motivoTrim.length > 1000) {
      setMotivoError('El motivo no puede superar los 1000 caracteres.');
      return;
    }

    try {
      setLoadingRechazo(true);
      const token = localStorage.getItem('token');

      await api.put(
        `/turnos/${turnoRechazo.id}/estado`,
        { estado: 'rechazado', devolucion: motivoTrim },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setPinnedIds(prev => {
        const next = new Set(prev);
        next.delete(turnoRechazo.id);
        return next;
      });
      setPinnedCache(prev => {
        const next = { ...prev };
        delete next[turnoRechazo.id];
        return next;
      });

      setMensaje('Turno rechazado correctamente.');
      setModalRechazoVisible(false);
      setTurnoRechazo(null);
      setMotivoRechazo('');
      await fetchTurnosPendientes();
    } catch (error) {
      console.error('❌ Error al rechazar el turno:', error);
      setError('Error al rechazar el turno.');
    } finally {
      setLoadingRechazo(false);
    }
  };

  const abrirModalRechazo = (turno) => {
    setTurnoRechazo(turno);
    setMotivoRechazo('');
    setMotivoError('');
    setModalRechazoVisible(true);
  };

  const cargarHorasDisponibles = useCallback(async (date, horaPreseleccionada = '') => {
    if (!date) {
      setHorasDisponibles([]);
      return;
    }

    try {
      const response = await api.get(`/turnos/disponibles?fecha=${date.toISOString().split('T')[0]}`);
      const horas = asegurarHoraEnLista(Array.isArray(response.data.horas) ? response.data.horas : [], horaPreseleccionada);
      setHorasDisponibles(horas);
    } catch (error) {
      console.error('❌ Error al obtener horarios para edición:', error);
      setError('Error al obtener horarios para la fecha seleccionada.');
      setHorasDisponibles([]);
    }
  }, []);

  const handleFechaChange = async (date) => {
    setTurnoSeleccionado(prev => ({ ...prev, fecha_turno: date, hora_desde: '', hora_hasta: '' }));
    setHorasDisponibles([]);
    await cargarHorasDisponibles(date);
  };

  const abrirModalEdicion = async (turno) => {
    const docs = getDocs(turno, { pinnedCacheRef, turnosRef });
    const fechaTurno =
      turno?.fecha_turno instanceof Date
        ? turno.fecha_turno
        : (turno?.fecha_turno ? new Date(`${turno.fecha_turno}T00:00:00`) : null);

    setPinnedCache(prev => ({
      ...prev,
      [turno.id]: {
        ...(prev[turno.id] || {}),
        documentos: docs,
        transportista: turno.transportista || (prev[turno.id]?.transportista),
        proveedor_bultos: Array.isArray(turno.proveedor_bultos) && turno.proveedor_bultos.length > 0
          ? turno.proveedor_bultos
          : (prev[turno.id]?.proveedor_bultos || [])
      }
    }));

    setTurnoSeleccionado({ ...turno, fecha_turno: fechaTurno, documentos: docs });
    setDevolucion(turno.devolucion || '');
    setModalEdicionVisible(true);
    await cargarHorasDisponibles(fechaTurno, turno.hora_desde || '');
  };

  const handleEditarTurno = (campo, valor) => {
    setTurnoSeleccionado(prev => ({ ...prev, [campo]: valor }));
  };

  const guardarEdicionTurno = async () => {
    if (!turnoSeleccionado) return;
    try {
      const token = localStorage.getItem('token');

      await api.put(`/turnos/${turnoSeleccionado.id}`, {
        fecha_turno: turnoSeleccionado.fecha_turno.toISOString().split('T')[0],
        hora_desde: turnoSeleccionado.hora_desde,
        hora_hasta: turnoSeleccionado.hora_hasta,
        devolucion,
        estado: 'pendiente_confirmacion_usuario'
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setTurnos(prev => prev.map(t =>
        t.id === turnoSeleccionado.id
          ? {
              ...t,
              fecha_turno: new Date(turnoSeleccionado.fecha_turno),
              hora_desde: turnoSeleccionado.hora_desde,
              hora_hasta: turnoSeleccionado.hora_hasta,
              devolucion,
              estado: 'pendiente_confirmacion_usuario',
              transportista: t.transportista || turnoSeleccionado.transportista,
              proveedor_bultos: Array.isArray(t.proveedor_bultos) && t.proveedor_bultos.length > 0
                ? t.proveedor_bultos
                : (Array.isArray(turnoSeleccionado.proveedor_bultos) ? turnoSeleccionado.proveedor_bultos : []),
              documentos: (() => {
                const vistos = getDocs(t, { pinnedCacheRef, turnosRef });
                const edit = Array.isArray(turnoSeleccionado.documentos) ? turnoSeleccionado.documentos : undefined;
                return (vistos && vistos.length > 0) ? vistos : (edit || []);
              })()
            }
          : t
      ));

      setPinnedIds(prev => {
        const next = new Set(prev);
        next.add(turnoSeleccionado.id);
        return next;
      });
      setPinnedCache(prev => ({
        ...prev,
        [turnoSeleccionado.id]: normalizarTurno({
          ...turnoSeleccionado,
          estado: 'pendiente_confirmacion_usuario',
          devolucion,
          documentos: getDocs(turnoSeleccionado, { pinnedCacheRef, turnosRef })
        })
      }));

      setMensaje('Turno editado. Ahora está pendiente de confirmación del usuario.');
      setModalEdicionVisible(false);
    } catch (error) {
      console.error('❌ Error al actualizar el turno:', error);
      setError('Error al actualizar el turno.');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('adminTurnos.pinnedIds');
    localStorage.removeItem('adminTurnos.pinnedCache');
    navigate('/');
  };

  // UI
  return (
    <div className="page-shell">
      <div className="d-flex justify-content-between align-items-center">
        <h2>Administración de Turnos</h2>
        <div className="d-flex align-items-center gap-2">
          <div className="form-check form-switch me-2 d-flex align-items-center">
            <input
              className="form-check-input"
              type="checkbox"
              id="autoRefresh"
              checked={autoRefresh}
              onChange={() => setAutoRefresh(v => !v)}
            />
            <label className="form-check-label ms-2" htmlFor="autoRefresh">
              Auto-refresh
            </label>
          </div>
          <select
            className="form-select form-select-sm me-2"
            style={{ width: 80 }}
            value={refreshMs}
            onChange={(e) => setRefreshMs(Number(e.target.value))}
            disabled={!autoRefresh}
            title="Período de actualización automática"
          >
            <option value={30000}>Cada 30s</option>
            <option value={60000}>Cada 60s</option>
            <option value={120000}>Cada 2 min</option>
            <option value={300000}>Cada 5 min</option>
          </select>
          <button
            className="btn btn-sm btn-outline-secondary me-2"
            onClick={fetchTurnosPendientes}
            disabled={loadingLista}
            title="Refrescar ahora"
          >
            {loadingLista ? '⏳ Refrescando…' : '🔄 Refrescar'}
          </button>

          <button className="btn btn-outline-primary me-2" onClick={() => navigate('/admin/calendario')}>
            🗓️ Ver Calendario
          </button>
          <button className="btn btn-outline-success me-2" onClick={() => navigate('/admin/turnos-aprobados')}>
            ✅ Ver Turnos Aprobados
          </button>
          <button className="btn btn-outline-warning me-2" onClick={() => navigate('/admin/crear-turno-transportista')}>
            ➕ Turno Transportista
          </button>
          <button className="btn btn-outline-info me-2" onClick={() => navigate('/admin/crear-turno-proveedor')}>
            ➕ Turno Proveedor
          </button>
          <button className="btn btn-outline-dark me-2" onClick={() => navigate('/admin/usuarios')}>
            Usuarios
          </button>
          <button className="btn btn-danger" onClick={handleLogout}>
            🚪 Cerrar Sesión
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger mt-3">{error}</div>}
      {mensaje && <div className="alert alert-success mt-3">{mensaje}</div>}

      {turnos.length === 0 ? (
        <p className="text-center mt-3">{loadingLista ? 'Cargando…' : 'No hay turnos pendientes.'}</p>
      ) : (
        <table className="table table-striped table-bordered text-center mt-3">
          <thead className="table-dark">
            <tr>
              <th>ID</th>
              <th>Solicitado el</th>
              <th>Fecha</th>
              <th>Hora Desde</th>
              <th>Hora Hasta</th>
              <th>Transportista/Proveedor</th>
              <th>Tipo Solicitante</th>
              <th style={{minWidth: '200px' }}>Archivos</th>
              <th>Proveedor, Facturas y Bultos</th>
              <th>Estado</th>
              <th>Observaciones</th>
              <th style={{minWidth: '300px' }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
          {turnos.map((turno) => {
            const tipoSol = getTipoSolicitante(turno);

            const nombreTP =
              turno.transportista?.nombre ||
              (Array.isArray(turno.proveedor_bultos) && turno.proveedor_bultos[0]?.nombre) ||
              'No asignado';

            return (
              <tr key={turno.id}>
                <td>
                  {turno.id}
                  {pinnedIds.has(turno.id) && <span title="Fijado"> 📌</span>}
                </td>
                <td>{turno.createdAt ? new Date(turno.createdAt).toLocaleString() : 'N/D'}</td>
                <td>{turno.fecha_turno ? new Date(turno.fecha_turno).toLocaleDateString() : 'Fecha no disponible'}</td>
                <td>{turno.hora_desde || 'No especificada'}</td>
                <td>{turno.hora_hasta || 'No especificada'}</td>

                {/*Transportista/Proveedor*/}
                <td>{nombreTP}</td>

                {/*Tipo Solicitante*/}
                <td>
                  <span
                    className={`badge ${tipoSol.code === 'P' ? 'bg-secondary' : 'bg-dark'}`}
                    title={tipoSol.text}
                  >
                    {tipoSol.text}
                  </span>
                </td>

                <td className="text-start">
                  <button
                    className="btn btn-sm btn-outline-secondary mb-2"
                    onClick={() => toggleExpand(turno.id + '-docs')}
                  >
                    {expandedRows.includes(turno.id + '-docs') ? '🔽 Ver menos' : '▶️ Ver archivos'}
                  </button>

                  {expandedRows.includes(turno.id + '-docs') && (() => {
                    const docs = getDocs(turno, { pinnedCacheRef, turnosRef });
                    return (
                      <ul className="list-unstyled">
                        {docs.length > 0 ? docs.map((doc, index) => (
                          <li key={index}>
                            <a href={doc.ruta} target="_blank" rel="noopener noreferrer">
                              📄 {doc.nombre_original}
                            </a>
                          </li>
                        )) : (
                          <li className="text-muted">Sin documentos</li>
                        )}
                      </ul>
                    );
                  })()}
                </td>

                <td className="text-start">
                  <button
                    className="btn btn-sm btn-outline-secondary mb-2"
                    onClick={() => toggleExpand(turno.id)}
                  >
                    {expandedRows.includes(turno.id) ? '🔽 Ocultar Detalle' : '▶️ Ver Detalle'}
                  </button>

                  {expandedRows.includes(turno.id) && (
                    <>
                      {Array.isArray(turno.proveedor_bultos) && turno.proveedor_bultos.length > 0 ? (
                        turno.proveedor_bultos.map((proveedor, index) => (
                          <div key={index} className="mb-2">
                            <strong>{proveedor.nombre}</strong>
                            <ul>
                              <li><strong>Facturas:</strong> {proveedor.facturas?.length ? proveedor.facturas.join(', ') : 'No hay facturas'}</li>
                              <li>
                                <strong>Bultos:</strong>
                                {Object.entries(proveedor.bultos || {}).map(([tipo, cantidad]) =>
                                  cantidad > 0 ? (<li key={tipo}><strong>{tipo}:</strong> {cantidad}</li>) : null
                                )}
                              </li>
                              <li><strong>Orden de Compra:</strong> {proveedor.orden_compra || 'No informada'}</li>
                              <li><strong>Remito:</strong> {proveedor.remito || 'No informado'}</li>
                            </ul>
                          </div>
                        ))
                      ) : <span className="text-muted">No hay proveedores asignados</span>}
                    </>
                  )}
                </td>

                <td>
                  <span className={`badge ${
                    turno.estado === 'aprobado' ? 'bg-success' :
                    turno.estado === 'rechazado' ? 'bg-danger' :
                    turno.estado === 'pendiente_confirmacion_usuario' ? 'bg-info' :
                    turno.estado === 'pendiente_admin' ? 'bg-primary' :
                    'bg-warning'
                  }`}>
                    {turno.estado === 'pendiente_confirmacion_usuario' 
                      ? 'Pendiente de confirmación del usuario' 
                      : turno.estado}
                  </span>
                </td>

                <td>{turno.observaciones || 'Sin observaciones'}</td>

                <td>
                  {turno.estado === 'pendiente_admin' ? (
                    <>
                      <button className="btn btn-success me-2" onClick={() => aprobarTurno(turno.id)}>✅ Aprobar</button>
                      <button className="btn btn-danger" onClick={() => abrirModalRechazo(turno)}>❌ Rechazar</button>
                    </>
                  ) : (
                    <>
                      <button className="btn btn-sm btn-primary me-2" onClick={() => abrirModalEdicion(turno)}>✏️ Editar</button>
                      <button className="btn btn-sm btn-success me-2" onClick={() => aprobarTurno(turno.id)}>✅ Aprobar</button>
                      <button className="btn btn-sm btn-danger" onClick={() => abrirModalRechazo(turno)}>❌ Rechazar</button>
                    </>
                  )}
                </td>
              </tr>
            );
          })}
          </tbody>
        </table>
      )}

      {modalEdicionVisible && turnoSeleccionado && (
        <div className="modal d-block">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Editar Turno</h5>
                <button type="button" className="btn-close" onClick={() => setModalEdicionVisible(false)}></button>
              </div>
              <div className="modal-body">
                <label>Fecha del Turno:</label>
                <DatePicker
                  selected={turnoSeleccionado.fecha_turno}
                  onChange={handleFechaChange}
                  includeDates={fechasDisponibles}
                  dateFormat="yyyy-MM-dd"
                  className="form-control"
                  required
                />

                <br />
                <label>Hora Desde:</label>
                <select className="form-control" value={turnoSeleccionado.hora_desde} onChange={(e) => handleEditarTurno('hora_desde', e.target.value)}>
                  <option value="">Selecciona una hora</option>
                  {horasDisponibles.map((hora, index) => (
                    <option key={index} value={hora}>{hora}</option>
                  ))}
                </select>

                <label>Hora Hasta:</label>
                <select className="form-control" value={turnoSeleccionado.hora_hasta} onChange={(e) => handleEditarTurno('hora_hasta', e.target.value)}>
                  <option value="">Selecciona una hora</option>
                  {horasDisponibles.filter(h => h > turnoSeleccionado.hora_desde).map((hora, index) => (
                    <option key={index} value={hora}>{hora}</option>
                  ))}
                </select>

                <label>Devolución:</label>
                <textarea className="form-control" value={devolucion} onChange={(e) => setDevolucion(e.target.value)}></textarea>
                </div>

              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setModalEdicionVisible(false)}>Cancelar</button>
                <button className="btn btn-success" onClick={guardarEdicionTurno}>Guardar Cambios</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {modalRechazoVisible && turnoRechazo && (
        <div className="modal d-block" role="dialog" aria-modal="true" onKeyDown={(e)=>{ if(e.key==='Escape'){ setModalRechazoVisible(false);} }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header bg-danger text-white">
                <h5 className="modal-title">Rechazar Turno #{turnoRechazo.id}</h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setModalRechazoVisible(false)}
                  aria-label="Cerrar"
                />
              </div>

              <div className="modal-body">
                <p className="mb-2">
                  Estás por rechazar el turno de <strong>{turnoRechazo.transportista?.nombre || 'Transportista/Proveedor'}</strong>
                  {turnoRechazo.fecha_turno ? <> del <strong>{new Date(turnoRechazo.fecha_turno).toLocaleDateString()}</strong></> : null}
                  {turnoRechazo.hora_desde ? <> a las <strong>{turnoRechazo.hora_desde}</strong></> : null}.
                </p>

                <label className="form-label">Motivo del rechazo <span className="text-danger">*</span></label>
                <textarea
                  className={`form-control ${motivoError ? 'is-invalid' : ''}`}
                  rows={4}
                  placeholder="Ej.: Falta documentación obligatoria, OC inválida, no hay disponibilidad, etc."
                  value={motivoRechazo}
                  onChange={(e) => {
                    setMotivoRechazo(e.target.value);
                    if (motivoError) setMotivoError('');
                  }}
                  maxLength={1000}
                  autoFocus
                />
                <div className="form-text d-flex justify-content-between">
                  <span>Agrega detalles claros para el proveedor/transportista.</span>
                  <span>{motivoRechazo.length}/1000</span>
                </div>
                {motivoError && <div className="invalid-feedback d-block">{motivoError}</div>}
              </div>

              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setModalRechazoVisible(false)}>Cancelar</button>
                <button
                  className="btn btn-danger"
                  onClick={confirmarRechazo}
                  disabled={loadingRechazo || (motivoRechazo || '').trim().length < 5}
                >
                  {loadingRechazo ? 'Procesando...' : 'Confirmar Rechazo'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTurnos;
